import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { AppDispatch, RootState } from '../../src/redux/store';
import {
  recordAxisFlip,
  recordEmptyPlan,
  setGeneratedWeek,
  selectOnboardingAnswers,
  selectOnboardingCompleted,
  selectOnboardingSafety,
} from '../../src/redux/onboardingSlice';
import {
  flipTasteAxis,
  setDefaultFamilySize,
  selectDefaultFamilySize,
  selectMealSchedule,
  selectTasteAxes,
} from '../../src/redux/userPreferencesSlice';
import { setWeekPortions } from '../../src/redux/mealPlanUISlice';
import { selectAllRecipeMetadata } from '../../src/redux/recipeMetadataSlice';
import { buildAllergenTerms } from '../../src/services/allergenMatcher';
import { AXES, type Axis, type TasteAxes } from '../../src/services/tasteAxes';
import {
  buildOnboardingWeek,
  localizeOnboardingTitles,
  onboardingWeekStart,
  withServings,
  type OnboardingWeekResult,
} from '../../src/services/onboardingWeek';
import recipeTranslationService from '../../src/services/recipeTranslationService';
import { loadPlannerLibrary, ONBOARDING_LIBRARY_WAIT_MS } from '../../src/services/plannerLibrary';
import { todayIso } from '../../src/utils/localDate';
import { OnboardingFrame, ONBOARDING_DESKTOP_MIN } from '../../src/components/onboarding/OnboardingFrame';
import { DuChefBubble } from '../../src/components/onboarding/DuChefBubble';
import { AxisRow } from '../../src/components/onboarding/AxisRow';
import { AllergenChip } from '../../src/components/onboarding/AllergenChip';
import { PeopleStepper } from '../../src/components/onboarding/PeopleStepper';
import { ProfileBeat, type BeatReading } from '../../src/components/onboarding/ProfileBeat';
import { Paprika } from '../../src/styles/theme';

/** The shortest hold when a week has to be rebuilt after « C'est moi ». */
const REBUILD_MIN_MS = 900;
/**
 * How long the beat may wait for the direction screen's titles to come back in
 * the cook's language (#721). Most are stored already and return in a few
 * hundred milliseconds; one the translate endpoint has to make can take ~2 s.
 * Past this the screen shows the original title and the planner catches up.
 */
const TITLE_WAIT_MS = 2500;

/** A built week and what it was built for. */
interface BuiltWeek {
  key: string;
  weekStart: string;
  result: OnboardingWeekResult;
}
const axesKey = (a: TasteAxes) => `${a.base}|${a.time}|${a.heat}`;

/** The other reading of each axis, for the pill on the right of the row. */
const OPPOSITE: { [A in Axis]: Record<TasteAxes[A], TasteAxes[A]> } = {
  base: { protein: 'vegetal', vegetal: 'protein' },
  time: { quick: 'long', long: 'quick' },
  heat: { bold: 'mild', mild: 'bold' },
};

/** Literal keys so the orphan-key guard sees every one of them. */
const CHIP_LABEL: { [A in Axis]: Record<TasteAxes[A], string> } = {
  base: { protein: 'onboarding.reading.chip.base.protein', vegetal: 'onboarding.reading.chip.base.vegetal' },
  time: { quick: 'onboarding.reading.chip.time.quick', long: 'onboarding.reading.chip.time.long' },
  heat: { bold: 'onboarding.reading.chip.heat.bold', mild: 'onboarding.reading.chip.heat.mild' },
};
const AXIS_NAME: Record<Axis, string> = {
  base: 'onboarding.profile.axis.base',
  time: 'onboarding.profile.axis.time',
  heat: 'onboarding.profile.axis.heat',
};
const ALLERGEN_LABEL: Record<string, string> = {
  arachides: 'onboarding.safety.allergen.arachides',
  noix: 'onboarding.safety.allergen.noix',
  crustaces: 'onboarding.safety.allergen.crustaces',
  lactose: 'onboarding.safety.allergen.lactose',
  gluten: 'onboarding.safety.allergen.gluten',
  oeufs: 'onboarding.safety.allergen.oeufs',
  soja: 'onboarding.safety.allergen.soja',
  poisson: 'onboarding.safety.allergen.poisson',
  sesame: 'onboarding.safety.allergen.sesame',
};

/**
 * Route /onboarding/profile — « Est-ce que ça vous ressemble ? ». How many
 * people they cook for, the three axes read back as rows the cook can flip,
 * and the allergies declared a moment ago with a way back to change them.
 * Arriving from the allergies, it opens on the profile beat, which reads the
 * answers back one at a time while the first week is built behind it (#721).
 * Confirming then only re-sizes that week to the portions — a flipped row
 * rebuilds it — and the direction screen asks what to do with it.
 */
export default function OnboardingProfile() {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const { t, i18n } = useTranslation();
  const { width } = useWindowDimensions();
  const desktop = width >= ONBOARDING_DESKTOP_MIN;

  const completed = useSelector(selectOnboardingCompleted);
  const answers = useSelector(selectOnboardingAnswers);
  const safety = useSelector(selectOnboardingSafety);
  const axes = useSelector(selectTasteAxes);
  const savedPeople = useSelector(selectDefaultFamilySize);

  const [people, setPeople] = useState<number>(savedPeople);
  // What the stepper opened on, for `people_changed`. Not `savedPeople`:
  // confirming writes it, so a retry after a failed build would read `false`.
  const openedWith = useRef(savedPeople);
  // Arriving from the allergies step (`?beat=1`) opens on the intro: the
  // answers read back one by one while the week is built behind it. A reload
  // or a way back here opens straight on the profile.
  const { beat: beatParam } = useLocalSearchParams<{ beat?: string }>();
  const [beat, setBeat] = useState<'hidden' | 'intro' | 'visible' | 'error' | 'empty'>(beatParam === '1' ? 'intro' : 'hidden');
  const [introPlayed, setIntroPlayed] = useState(false);
  const [closing, setClosing] = useState(false);
  /** The week built behind the intro, keyed on the axes it was built for. */
  const prebuilt = useRef<Promise<BuiltWeek> | null>(null);
  const confirming = useRef(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // Only the focused screen may redirect (see the quiz screen).
  const focused = useIsFocused();
  useEffect(() => {
    if (!focused) return;
    if (completed) router.replace('/(app)');
    else if (answers.length < 3) router.replace('/onboarding');
    else if (!safety || !axes) router.replace('/onboarding/safety');
  }, [focused, completed, answers.length, safety, axes, router]);

  const flip = useCallback((axis: Axis) => {
    dispatch(flipTasteAxis(axis));
    dispatch(recordAxisFlip({ axis }));
  }, [dispatch]);

  const editSafety = useCallback(() => {
    if (router.canGoBack()) router.back();
    else router.push('/onboarding/safety');
  }, [router]);

  /**
   * Picks the week, fetches its details and puts the titles the direction
   * screen shows in the cook's language. Built for the portions on the
   * stepper; `withServings` re-sizes it if the cook changes them.
   */
  const buildWeek = useCallback(async (): Promise<BuiltWeek> => {
    if (!safety || !axes) throw new Error('profile incomplete');
    const started = Date.now();
    const state = store.getState();
    const currentAxes = selectTasteAxes(state) ?? axes;
    const weekStart = onboardingWeekStart();
    const language = i18n.language?.startsWith('fr') ? 'fr' : 'en';
    // The whole catalogue, as every other generator reads it; the bundled
    // recipes if it is slower than the reveal can wait.
    const recipes = await loadPlannerLibrary({
      language, uid: state.auth?.user?.uid, indexed: state.recipeIndex?.entries, deadlineMs: ONBOARDING_LIBRARY_WAIT_MS,
    });
    const built = await buildOnboardingWeek({
      weekStart,
      recipes,
      metadata: selectAllRecipeMetadata(state),
      axes: currentAxes,
      allergenTerms: buildAllergenTerms(safety.keys, safety.custom),
      schedule: selectMealSchedule(state),
      servings: people,
      todayIso: todayIso(),
      language,
    });
    if (built.meals.length === 0) return { key: axesKey(currentAxes), weekStart, result: built };
    const { meals, tonight } = await localizeOnboardingTitles(
      built.meals,
      built.tonight,
      language,
      (id, title, from, to) => recipeTranslationService.translateTitle(id, title, from, to),
      Math.max(0, TITLE_WAIT_MS - (Date.now() - started)),
      (title) => recipeTranslationService.detectLanguageFromTitle(title),
    );
    return { key: axesKey(currentAxes), weekStart, result: { ...built, meals, tonight } };
  }, [safety, axes, people, store, i18n.language]);

  // An empty week is not an error — nothing threw — so it used to be committed
  // in silence and the cook landed on a blank planner. Five onboardings did
  // exactly that. Hold them on the beat and name the reason.
  const holdOnEmpty = useCallback((built: BuiltWeek) => {
    const { diagnostics } = built.result;
    const reason = diagnostics.recipesConsidered === 0 ? 'no-recipes' : 'exclusions';
    dispatch(recordEmptyPlan({ reason, diagnostics }));
    // 'no-recipes' means the library never loaded, which a retry can fix.
    // 'exclusions' is deterministic: retrying returns the same nothing, so the
    // only way forward is back to the safety step.
    if (mounted.current) setBeat(reason === 'no-recipes' ? 'error' : 'empty');
  }, [dispatch]);

  // The intro builds the week while it plays, so « C'est moi » has nothing left to wait for.
  useEffect(() => {
    if (beat === 'intro' && !prebuilt.current && safety && axes) {
      prebuilt.current = buildWeek();
      prebuilt.current.catch(() => {}); // handled where it is awaited
    }
  }, [beat, safety, axes, buildWeek]);

  // Leave the intro once it has played AND the week is ready — whichever is last.
  useEffect(() => {
    if (!introPlayed || !prebuilt.current) return;
    let live = true;
    prebuilt.current.then(
      (built) => {
        if (!live || !mounted.current) return;
        if (built.result.meals.length === 0) holdOnEmpty(built);
        else setClosing(true);
      },
      (error) => {
        console.error('[onboarding] week generation failed', error);
        prebuilt.current = null;
        if (live && mounted.current) setBeat('error');
      },
    );
    return () => { live = false; };
  }, [introPlayed, holdOnEmpty]);

  const confirm = useCallback(async () => {
    if (!safety || !axes || confirming.current) return;
    confirming.current = true;
    dispatch(setDefaultFamilySize(people));
    dispatch(setWeekPortions(people));
    const key = axesKey(selectTasteAxes(store.getState()) ?? axes);
    const started = Date.now();
    try {
      // The week from the intro still holds unless a row was flipped since.
      let built = prebuilt.current ? await prebuilt.current.catch(() => null) : null;
      const rebuilt = !built || built.key !== key || built.result.meals.length === 0;
      if (rebuilt) {
        setBeat('visible');
        prebuilt.current = buildWeek();
        built = await prebuilt.current;
      }
      if (built!.result.meals.length === 0) {
        await new Promise((r) => setTimeout(r, Math.max(0, REBUILD_MIN_MS - (Date.now() - started))));
        holdOnEmpty(built!);
        return;
      }
      const { result, weekStart } = built!;
      dispatch(setGeneratedWeek({
        meals: withServings(result.meals, people),
        weekStart,
        diagnostics: result.diagnostics,
        tonight: result.tonight,
        people: { count: people, changed: people !== openedWith.current },
      }));
      if (rebuilt) await new Promise((r) => setTimeout(r, Math.max(0, REBUILD_MIN_MS - (Date.now() - started))));
      if (mounted.current) router.replace('/onboarding/direction');
    } catch (error) {
      console.error('[onboarding] week generation failed', error);
      prebuilt.current = null;
      if (mounted.current) setBeat('error');
    } finally {
      confirming.current = false;
    }
  }, [safety, axes, people, dispatch, store, router, buildWeek, holdOnEmpty]);

  const bubble = <DuChefBubble tone="say" testID="onb-profile-bubble">{t('onboarding.profile.bubble')}</DuChefBubble>;
  const confirmButton = (
    <Pressable
      onPress={confirm}
      disabled={beat !== 'hidden'}
      testID="onb-profile-confirm"
      accessibilityRole="button"
      style={styles.confirm}
    >
      <Text style={styles.confirmText}>{t('onboarding.profile.confirm')}</Text>
    </Pressable>
  );

  const declared = safety ? [...safety.keys.map((k) => t(ALLERGEN_LABEL[k] ?? k)), ...safety.custom] : [];
  const reading: BeatReading | undefined = axes
    ? {
      chips: AXES.map((axis) => t(CHIP_LABEL[axis][axes[axis] as never])),
      allergies: declared.length
        ? t('onboarding.safety.beatAllergies', { list: declared.map((d) => d.toLocaleLowerCase(i18n.language)).join(', ') })
        : t('onboarding.safety.beatNoAllergies'),
    }
    : undefined;

  // Desktop: a focused column in the middle of the pane, the question above
  // it and « C'est moi » right under it, like the allergies screen. Phone:
  // the bubble on top, the same column at full width, the button pinned.
  const body = (
    <View style={[styles.body, desktop && styles.bodyDesktop]}>
      <Text style={[styles.title, desktop && styles.titleDesktop]}>{t('onboarding.profile.title')}</Text>

      {/* First, not last: on a 393×665 phone the last section sat under the
          pinned « C'est moi », so cooks confirmed the default four without
          ever seeing the question — and the first grocery list was sized for
          four (#720). Nothing on the page may push this below the fold.
          The same card as an AxisRow, so its content lines up with theirs. */}
      <View style={styles.peopleCard} testID="onb-profile-people-card">
        <Text style={styles.peopleName}>{t('onboarding.profile.peopleKicker')}</Text>
        <PeopleStepper
          value={people}
          onChange={setPeople}
          unit={t('onboarding.profile.people', { count: people })}
          fewerLabel={t('onboarding.profile.fewer')}
          moreLabel={t('onboarding.profile.more')}
          testID="onb-profile-people"
        />
      </View>

      <View style={styles.section} testID="onb-profile-axes">
        {axes && AXES.map((axis) => (
          <AxisRow
            key={axis}
            testID={`onb-profile-axis-${axis}`}
            name={t(AXIS_NAME[axis])}
            current={t(CHIP_LABEL[axis][axes[axis] as never])}
            opposite={t(CHIP_LABEL[axis][OPPOSITE[axis][axes[axis] as never] as never])}
            onFlip={() => flip(axis)}
            compact={!desktop}
          />
        ))}
        <Text style={styles.hint}>{t('onboarding.profile.flipHint')}</Text>
      </View>

      <View style={styles.section}>
        <View style={styles.kickerRow}>
          <Text style={styles.kicker}>{t('onboarding.profile.allergiesKicker')}</Text>
          <Pressable onPress={editSafety} testID="onb-profile-edit-safety" accessibilityRole="link" hitSlop={8}>
            <Text style={styles.link}>{t('onboarding.profile.edit')}</Text>
          </Pressable>
        </View>
        <View style={styles.chips} testID="onb-profile-allergens">
          {declared.length === 0
            ? <Text style={styles.none}>{t('onboarding.profile.none')}</Text>
            : declared.map((label, i) => (
              <AllergenChip key={`${label}-${i}`} label={label} selected onPress={editSafety} testID={`onb-profile-allergen-${i}`} />
            ))}
        </View>
      </View>

      {desktop && <View style={styles.deskConfirm}>{confirmButton}</View>}
    </View>
  );

  return (
    <View style={styles.root}>
      <OnboardingFrame
        kicker={t('onboarding.profile.kicker')}
        rail={bubble}
        footer={confirmButton}
        railFooter={desktop ? <View /> : undefined}
        scroll
      >
        {desktop ? <View style={styles.deskCenter}>{body}</View> : body}
      </OnboardingFrame>
      {beat !== 'hidden' && (
        <ProfileBeat
          state={beat}
          reading={reading}
          onIntroDone={() => setIntroPlayed(true)}
          closing={closing}
          onClosed={() => {
            setClosing(false);
            setBeat('hidden');
            // A reload from here opens on the profile, not on the intro again.
            router.setParams({ beat: undefined });
          }}
          onRetry={() => { setBeat('hidden'); void confirm(); }}
          onEditSafety={() => { setBeat('hidden'); editSafety(); }}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: Paprika.background },
  body: { gap: 22 },
  deskCenter: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  bodyDesktop: { width: '100%', maxWidth: 600, gap: 24 },
  title: { fontFamily: 'Newsreader-Regular', fontSize: 24, lineHeight: 28, color: Paprika.textPrimary },
  titleDesktop: { fontSize: 30, lineHeight: 36 },
  section: { gap: 10 },
  // Mirrors AxisRow's row + name styles: same surface, radius, padding and
  // label type, so the stepper sits on the content edge of the cards below.
  peopleCard: {
    gap: 10, paddingVertical: 12, paddingHorizontal: 14, borderRadius: 14,
    backgroundColor: Paprika.cardBg, borderWidth: 1, borderColor: Paprika.hairline,
    boxShadow: '0 6px 18px -10px rgba(82, 68, 60, 0.35)',
  },
  peopleName: { fontFamily: 'Inter-SemiBold', fontSize: 10.5, lineHeight: 15, letterSpacing: 1, textTransform: 'uppercase', color: Paprika.textMuted },
  hint: { fontFamily: 'Inter-Regular', fontSize: 12.5, color: Paprika.textMuted },
  kickerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  kicker: { flex: 1, fontFamily: 'Inter-SemiBold', fontSize: 11, lineHeight: 16, letterSpacing: 1.4, textTransform: 'uppercase', color: Paprika.textMuted },
  link: { fontFamily: 'Inter-SemiBold', fontSize: 13.5, color: Paprika.accent, textDecorationLine: 'underline' },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
  none: { fontFamily: 'Inter-Medium', fontSize: 14, color: Paprika.textPrimary },
  deskConfirm: { marginTop: 4 },
  confirm: { height: 50, borderRadius: 14, backgroundColor: Paprika.accent, alignItems: 'center', justifyContent: 'center' },
  confirmText: { fontFamily: 'Inter-SemiBold', fontSize: 15, color: '#ffffff' },
});
